'use client';

// Temporal Whispers — subtle Easter eggs for Maeve, who loves Cookie Run:
// Kingdom (the "Timeline of Fate" / Timekeeper Cookie arc) and Loki (the TVA).
//
// Pure decoration, entirely outside her story: never stored, embedded, or
// shown to the mascot. A faint line fades in somewhere on screen, lingers,
// and fades away. One is GUARANTEED each visit (so it never feels broken);
// after that they're random and sparse.

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

// She'll recognize these; a passer-by just sees flavor text about time.
const WHISPERS: { text: string; from: 'tva' | 'crk' }[] = [
  // Loki / the TVA
  { text: 'For all time. Always.', from: 'tva' },
  { text: 'The Sacred Timeline remains intact.', from: 'tva' },
  { text: 'A nexus event was detected… and quietly forgiven.', from: 'tva' },
  { text: 'Variant identified. Pruning postponed indefinitely.', from: 'tva' },
  { text: 'Miss Minutes is keeping an eye on things.', from: 'tva' },
  { text: 'This timeline branches with glorious purpose.', from: 'tva' },
  // Cookie Run: Kingdom — Timeline of Fate
  { text: 'The Time Balance Department thanks you for your service.', from: 'crk' },
  { text: 'Timekeeper Cookie steadies the flow of time.', from: 'crk' },
  { text: 'The Wheel of Fate keeps on turning.', from: 'crk' },
  { text: 'Shadow Milk Cookie tried to peek behind the clock. Shooed.', from: 'crk' },
  { text: 'Timeline stable… for now.', from: 'crk' },
];

// A few resting spots on the right side of the screen (clear of the left nav
// and the bottom-right mascot) so they "jump around" instead of always landing
// in the same corner.
const SPOTS: React.CSSProperties[] = [
  { top: 72, right: 24 },
  { top: 116, right: 52 },
  { top: 168, right: 28 },
  { top: 92, right: 132 },
  { top: 210, right: 60 },
];

const APPEAR_CHANCE = 0.3;          // per page change, after the first
const COOLDOWN_MS = 90 * 1000;      // at most once every ~90s
const HOLD_MS = 7000;

export default function TemporalWhispers() {
  const pathname = usePathname();
  const [whisper, setWhisper] = useState<(typeof WHISPERS)[number] | null>(null);
  const [spot, setSpot] = useState<React.CSSProperties>(SPOTS[0]);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (pathname === '/login') return;
    // Runs client-side only, so hydration stays deterministic.
    let forced = false;
    try {
      // First eligible view of a visit is guaranteed — proves it's alive.
      forced = !sessionStorage.getItem('whisper:seen');
      if (!forced) {
        if (Math.random() > APPEAR_CHANCE) return;
        const last = Number(localStorage.getItem('whisper:last') || 0);
        if (Date.now() - last < COOLDOWN_MS) return;
      }
      sessionStorage.setItem('whisper:seen', '1');
      localStorage.setItem('whisper:last', String(Date.now()));
    } catch {
      if (!forced) return;
    }

    setWhisper(WHISPERS[Math.floor(Math.random() * WHISPERS.length)]);
    setSpot(SPOTS[Math.floor(Math.random() * SPOTS.length)]);
    const inT = setTimeout(() => setShown(true), 250);
    const outT = setTimeout(() => setShown(false), HOLD_MS);
    const rmT = setTimeout(() => { setShown(false); setWhisper(null); }, HOLD_MS + 1500);
    return () => { clearTimeout(inT); clearTimeout(outT); clearTimeout(rmT); };
  }, [pathname]);

  if (!whisper) return null;

  return (
    <div
      aria-hidden="true"
      className="fixed z-40 max-w-[230px] pointer-events-none select-none text-right"
      style={{
        ...spot,
        opacity: shown ? 0.62 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(-6px)',
        transition: 'opacity 1200ms ease, transform 1200ms ease',
      }}
    >
      <p className="text-[11px] font-mono italic leading-relaxed text-slate-300">
        <span className="mr-1.5 not-italic" style={{ color: whisper.from === 'tva' ? '#a184ef' : '#e3b34c' }}>✦</span>
        {whisper.text}
      </p>
    </div>
  );
}
